<template>
  <div class="row">
    <div class="columnBoxLeft">
      <label class="fileNameLabel">
        {{
          props.fileNumber === 0
            ? "No file chosen"
            : props.fileNumber === 1
              ? "1 file uploaded"
              : (props.type == "CR_SL" || props.type == "EP_CC") &&
                  props.field == "upload2"
                ? props.fileNumber === 20
                  ? "Maximum uploads reached."
                  : props.fileNumber + " files uploaded"
                : props.fileNumber === 10
                  ? "Maximum uploads reached."
                  : props.fileNumber + " files uploaded"
        }}
      </label>
    </div>

    <div v-if="deletes" class="columnBoxRight">
      <div class="columnRight">
        <template v-if="props.errorDisabledSubmitButton">
          <label class="uploadFile" :class="{ 'disabled-button': true }">
            <span class="button-label">{{
              t("ccOnBoarding.uploadDocument.uploadButton")
            }}</span>
            <Field
              type="file"
              :id="id"
              @input="!isBtnDisabled"
              name="uploadFile"
              multiple
              :accept="acceptFileTypes"
              @change="handleFileChange"
              style="opacity: 0; position: absolute; left: -9999px"
            />
          </label>
        </template>
        <template v-else>
          <label
            class="uploadFile"
            :class="{ 'disabled-button': isBtnDisabled }"
          >
            <span class="button-label">{{
              t("ccOnBoarding.uploadDocument.uploadButton")
            }}</span>
            <Field
              type="file"
              :id="id"
              @input="!isBtnDisabled"
              name="uploadFile"
              multiple
              :accept="acceptFileTypes"
              @change="handleFileChange"
              style="opacity: 0; position: absolute; left: -9999px"
            />
          </label>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useField, Field } from "vee-validate";
import i18n from "@/plugin/i18n";

const { t } = i18n.global;
const emit = defineEmits(["selected", "file"]);
const props = defineProps({
  field: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  fileHolderName: {
    type: Array,
    default: "",
  },
  fileHolder: {
    type: Array,
    default: "",
  },
  fileNumber: {
    type: Number,
    default: 0,
  },
  maxFileSizeMB: {
    type: Number,
    default: 5,
  },
  deletes: {
    type: Boolean,
    default: true,
  },
  uploadFileValidity: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
  },
  errorDisabledSubmitButton: {
    type: Boolean,
  },
});

const acceptFileTypes = "image/jpeg, image/jpg, application/pdf, image/png,";

const files = ref<
  Array<{ name: string; size: number; base64: string | ArrayBuffer | null }>
>([]);

const isBtnDisabled = computed(() => {
  return (props.type == "CR_SL" || props.type == "EP_CC") &&
    props.field == "upload2"
    ? props.fileNumber >= 20
    : props.fileNumber >= 10;
});

// Handle file change
const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (files && files.length > 0) {
    const file = files[0];
    props.uploadFileValidity.errorMessage = "";
    if (!isValidFileType(file)) {
      if (props.field == "upload1")
        props.uploadFileValidity.uploadFile1 = "invalidFileType";
      else if (props.field == "upload2")
        props.uploadFileValidity.uploadFile2 = "invalidFileType";
      else if (props.field == "upload4")
        props.uploadFileValidity.uploadFile4 = "invalidFileType";
      // Reset the input value if file type is invalid
      target.value = "";
    } else {
      props.uploadFileValidity.uploadFile1 = "";
      props.uploadFileValidity.uploadFile2 = "";
      props.uploadFileValidity.uploadFile4 = "";
    }
  }
};

// Check if the file type is valid
const isValidFileType = (file: File) => {
  const allowedTypes = acceptFileTypes.split(",").map((type) => type.trim());

  // Check if the MIME type matches
  const isMIMEValid = allowedTypes.includes(file.type);

  // Check file extension (in case file extension is different but MIME type is still valid)
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  const allowedExtensions = ["jpeg", "jpg", "png", "pdf"];

  const isExtensionValid = allowedExtensions.includes(fileExtension || "");

  // Return true if both MIME type and extension are valid
  return isMIMEValid && isExtensionValid;
};

// const previewFiles = () => {
//   const fileInput = document.getElementById(props.id) as HTMLInputElement;
//   const newFiles = fileInput.files;

//   if (newFiles) {
//     for (let i = 0; i < newFiles.length; i++) {
//       const file = newFiles[i];
//       const reader = new FileReader();

//       const maxSizeBytes = props.maxFileSizeMB * 1024 * 1024; // Convert MB to bytes
//       if (file.size > maxSizeBytes) {
//         alert(
//           `File "${file.name}" exceeds the maximum allowed size of ${props.maxFileSizeMB}MB.`
//         );
//         continue; // Skip this file and continue with the next one
//       }

//       reader.onload = () => {
//         const docs = {
//           name: file.name,
//           size: file.size,
//           base64: reader.result,
//         };

//         files.value.push(docs);
//       };

//       reader.readAsDataURL(file);
//     }

//     emit(
//       "selected",
//       files.value.map((file) => file.name)
//     );
//     emit("file", files.value);
//   }
// };
</script>

<style scoped>
.uploadFile {
  width: auto;
  padding: 8px 16px;
  border-radius: 25px;
  text-align: center;
  border: none;
  background-image: linear-gradient(90deg, #7653ff 0%, #8e4eff 3%, #ea27c2 45%);
  font:
    500 12px/130% "Outfit",
    sans-serif;
  color: #fff;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
  display: inline;
  display: inline-block;
  position: relative;
  cursor: pointer;
}

.columnBoxLeft {
  float: left;
  width: 70%;
  margin: auto 0;
  padding: 0;
}

.columnBoxRight {
  float: left;
  width: 30%;
  margin: auto 0;
  padding: 0;
}

.row {
  width: 100%;
  margin: auto;
}

.row:after {
  display: table;
  clear: both;
}

.columnRight {
  text-align: right;
}
.fileNameLabel {
  font:
    500 14px/130% "Outfit",
    sans-serif;
  color: rgba(0, 0, 0, 0.87);
}

.disabled-button {
  width: auto;
  padding: 8px 16px;
  border-radius: 25px;
  text-align: center;
  border: none;
  background: #dedede;
  font:
    500 12px/130% "Outfit",
    sans-serif;
  color: #fff;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
  display: inline;
  cursor: not-allowed;
  pointer-events: none;
  display: inline-block;
  position: relative;
}
</style>
